/**
 * BarReplayPanel — bar-by-bar chart replay controls.
 *
 * Provides VCR-style playback controls (play, pause, step forward, step back,
 * reset, seek) and a speed selector (0.5× … 10×) for replaying historical
 * price action one bar at a time.
 *
 * The progress bar is click-seekable.  All playback state is owned by the
 * parent (`ChartPanel`) and passed in as props; this component is purely
 * presentational.
 */
import React, { useState, useEffect, useRef, useCallback } from "react";

interface BarReplayPanelProps {
  totalBars: number;
  isPlaying: boolean;
  currentBar: number;
  speed: number;
  onPlay: () => void;
  onPause: () => void;
  onStep: () => void;
  onStepBack: () => void;
  onReset: () => void;
  onSeek: (bar: number) => void;
  onSpeedChange: (speed: number) => void;
  onExit: () => void;
  colorTheme: string;
}

const SPEED_OPTIONS = [
  { label: "0.5x", value: 2000 },
  { label: "1x", value: 1000 },
  { label: "2x", value: 500 },
  { label: "5x", value: 200 },
  { label: "10x", value: 100 },
];

const BarReplayPanel: React.FC<BarReplayPanelProps> = ({
  totalBars,
  isPlaying,
  currentBar,
  speed,
  onPlay,
  onPause,
  onStep,
  onStepBack,
  onReset,
  onSeek,
  onSpeedChange,
  onExit,
  colorTheme,
}) => {
  const progressRef = useRef<HTMLDivElement>(null);

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!progressRef.current || totalBars <= 0) return;
    const rect = progressRef.current.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    onSeek(Math.round(ratio * totalBars));
  };

  const pct = totalBars > 0 ? (currentBar / totalBars) * 100 : 0;

  return (
    <div className={`bar-replay-panel ${colorTheme}`}>
      <div className="brp-controls">
        <button className="brp-btn brp-exit" onClick={onExit} title="Exit Replay">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className="brp-separator" />

        <button className="brp-btn" onClick={onReset} title="Go to start">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
            <polygon points="19,20 9,12 19,4" fill="currentColor" />
            <line x1="5" y1="4" x2="5" y2="20" />
          </svg>
        </button>

        <button className="brp-btn" onClick={onStepBack} title="Step back">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
            <polygon points="17,18 9,12 17,6" fill="currentColor" />
            <line x1="7" y1="6" x2="7" y2="18" />
          </svg>
        </button>

        {isPlaying ? (
          <button className="brp-btn brp-play" onClick={onPause} title="Pause">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
              <rect x="6" y="4" width="4" height="16" rx="1" />
              <rect x="14" y="4" width="4" height="16" rx="1" />
            </svg>
          </button>
        ) : (
          <button className="brp-btn brp-play" onClick={onPlay} title="Play">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
              <polygon points="5,3 19,12 5,21" />
            </svg>
          </button>
        )}

        <button className="brp-btn" onClick={onStep} title="Step forward">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
            <polygon points="7,6 15,12 7,18" fill="currentColor" />
            <line x1="17" y1="6" x2="17" y2="18" />
          </svg>
        </button>

        <div className="brp-separator" />

        <div className="brp-speed">
          {SPEED_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              className={`brp-speed-btn ${speed === opt.value ? "active" : ""}`}
              onClick={() => onSpeedChange(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className="brp-separator" />

        <span className="brp-counter">
          Bar {currentBar} / {totalBars}
        </span>
      </div>

      <div className="brp-progress" ref={progressRef} onClick={handleProgressClick}>
        <div className="brp-progress-fill" style={{ width: `${pct}%` }} />
        <div className="brp-progress-thumb" style={{ left: `${pct}%` }} />
      </div>
    </div>
  );
};

export default BarReplayPanel;
